// ═══════════════════════════════════════════════════════════════════════════
// grainStorage.js — the storage-strategy SEAM (Kevin, 2026-08-18 20:40:25Z,
// PR#5: "add a two-choice storage-strategy seam (Deliver at Harvest / Has
// On-Farm Storage, optional capacity bushels) as a pure data/model seam —
// explicitly NO calendar deadlines, forced-sale %, or carrying-cost
// economics yet"). This file is deliberately thin: it validates and shapes
// whatever a farmer's `metadata.grainStorage` already holds, and nothing
// downstream reads it for anything but display. The "marketing clock"
// (position + time + market + basis + storage -> a real timing model,
// Kevin's own "Usset-style marketing-plan framework" reference) is a later,
// separate layer with its own methodology — this file is only the input it
// will eventually consume, not that model.
//
// PURE — no fetch, no React, no Supabase. Persistence is a caller concern
// (App.jsx writes farmers.metadata.grainStorage via the SAME PATCH-merge
// pattern already used for drpEstProd — see DRPProtectionPlanner's
// onPersist), matching cop/cash's existing "resolved outside, passed in"
// shape in grainAction.js.
// ═══════════════════════════════════════════════════════════════════════════

export const STORAGE_MODE = { DELIVER_AT_HARVEST: "deliver_at_harvest", HAS_STORAGE: "has_storage" };

// resolveGrainStorage — reads a farmer's already-fetched `metadata` (the
// SAME farmers.metadata jsonb column drpEstProd already lives in — no new
// column, no migration). Never fabricates a mode or a capacity: absent or
// malformed input reports known:false, exactly the "unknown, not zero/
// default" convention every other Position stage in this file family
// follows (§2.2 in ROADMAP.md — zero is never a substitute for unknown).
export function resolveGrainStorage(metadata) {
  const raw = metadata && metadata.grainStorage;
  if (!raw || typeof raw !== "object") {
    return { known: false, mode: null, capacityBu: null, updatedAt: null };
  }
  const mode = (raw.mode === STORAGE_MODE.DELIVER_AT_HARVEST || raw.mode === STORAGE_MODE.HAS_STORAGE) ? raw.mode : null;
  if (!mode) return { known: false, mode: null, capacityBu: null, updatedAt: null };
  // capacityBu is optional even when mode is known (a farmer may say "I have
  // storage" without stating bushels yet) — withheld (null) rather than
  // coerced to 0 or guessed, and only accepted as a real, positive, finite
  // number so a malformed value degrades to "not stated" instead of
  // rendering a garbage figure.
  const rawCap = raw.capacityBu;
  const capacityBu = (mode === STORAGE_MODE.HAS_STORAGE && rawCap != null && Number.isFinite(Number(rawCap)) && Number(rawCap) > 0)
    ? Number(rawCap) : null;
  return { known: true, mode, capacityBu, updatedAt: raw.updatedAt || null };
}

// deriveStorageExposure — the ONE mechanical split Kevin asked for 2026-08-18
// 21:45 ("storage capacity must become POSITION-relevant, without inventing
// deadlines"): given a resolved storage strategy + an expected-crop
// denominator, split expected production into what MUST leave at harvest
// (exceeds known capacity, or the farm has no storage at all) vs. what CAN be
// stored. This is arithmetic, not a recommendation — it feeds no status,
// sizing, or trigger anywhere; a future "marketing clock" model
// (grainMarketingClock.js) is the layer that will eventually turn this split
// into a timing claim, and it does not exist yet.
//
// UNKNOWN NEVER BECOMES ZERO (the one rule that matters here): an unstated
// expected crop, an unstated capacity, or a mode that was never chosen all
// return known:false with every derived bushel figure left null — never a
// fabricated 0 bu split that would read as "nothing needs storing."
// `deliver_at_harvest` is the one case that's genuinely, definitionally 100%/
// 0% rather than "unknown" — the mode itself states there is no storage to
// split against.
export function deriveStorageExposure({ mode, capacityBu, expectedCropBu } = {}) {
  const cropKnown = expectedCropBu != null && Number.isFinite(Number(expectedCropBu)) && Number(expectedCropBu) > 0;
  const expectedCropBuOut = cropKnown ? Number(expectedCropBu) : null;
  const capacityKnown = capacityBu != null && Number.isFinite(Number(capacityBu)) && Number(capacityBu) > 0;
  const capacityOut = capacityKnown ? Number(capacityBu) : null;

  const unknown = (storageCapacityBu = null) => ({
    known: false, expectedCropBu: expectedCropBuOut, storageCapacityBu,
    harvestDeliveryRequiredBu: null, storableBu: null,
    harvestDeliveryRequiredPct: null, storablePct: null,
  });

  if (!cropKnown || mode == null) return unknown(mode === STORAGE_MODE.HAS_STORAGE ? capacityOut : null);

  if (mode === STORAGE_MODE.DELIVER_AT_HARVEST) {
    return {
      known: true, expectedCropBu: expectedCropBuOut, storageCapacityBu: null,
      harvestDeliveryRequiredBu: expectedCropBuOut, storableBu: 0,
      harvestDeliveryRequiredPct: 100, storablePct: 0,
    };
  }

  // mode === HAS_STORAGE — a real split needs a real (>0) capacity number;
  // "has storage but I don't know how much" stays unknown rather than
  // assuming either 0 or "all of it" fits.
  if (!capacityKnown) return unknown(null);

  const storableBu = Math.min(expectedCropBuOut, capacityOut);
  const harvestDeliveryRequiredBu = Math.max(expectedCropBuOut - capacityOut, 0);
  return {
    known: true, expectedCropBu: expectedCropBuOut, storageCapacityBu: capacityOut,
    harvestDeliveryRequiredBu, storableBu,
    harvestDeliveryRequiredPct: +(harvestDeliveryRequiredBu / expectedCropBuOut * 100).toFixed(1),
    storablePct: +(storableBu / expectedCropBuOut * 100).toFixed(1),
  };
}
